/* ============================================================
   apiClient.ts — Authenticated fetch helper for the CADtomie API.
   ------------------------------------------------------------
   The backend lives at api.cadtomie.com (Render) and expects a
   Supabase JWT in the Authorization header on every protected
   endpoint. This wrapper:
     - resolves the API base URL from VITE_API_BASE_URL
     - pulls the current access token from the shared Supabase
       client
     - sets Authorization automatically
     - throws a typed error on non-2xx so callers can show
       reasonable messages without writing the same boilerplate.
   ============================================================ */

import { getAccessToken } from './supabase';

const DEFAULT_BASE = 'https://api.cadtomie.com';

export class ApiError extends Error {
  status: number;
  body: string;
  constructor(status: number, body: string, message?: string) {
    super(message ?? `API error ${status}`);
    this.status = status;
    this.body = body;
  }
}

function apiBase(): string {
  const fromEnv = import.meta.env.VITE_API_BASE_URL;
  if (fromEnv && typeof fromEnv === 'string') return fromEnv.replace(/\/$/, '');
  return DEFAULT_BASE;
}

export interface ApiFetchOptions extends Omit<RequestInit, 'headers'> {
  headers?: Record<string, string>;
  /** If true, omit Authorization even if a session exists. Default false. */
  anonymous?: boolean;
}

export async function apiFetch(path: string, opts: ApiFetchOptions = {}): Promise<Response> {
  const { anonymous, headers, ...rest } = opts;
  const url = `${apiBase()}${path.startsWith('/') ? path : `/${path}`}`;

  const finalHeaders: Record<string, string> = { ...(headers ?? {}) };
  if (!anonymous) {
    const token = await getAccessToken();
    if (token) finalHeaders.Authorization = `Bearer ${token}`;
  }
  if (rest.body && !finalHeaders['Content-Type']) {
    finalHeaders['Content-Type'] = 'application/json';
  }

  const resp = await fetch(url, { ...rest, headers: finalHeaders });
  if (!resp.ok) {
    let body = '';
    try {
      body = await resp.text();
    } catch {
      /* ignore */
    }
    throw new ApiError(resp.status, body);
  }
  return resp;
}
